import { ChainsSummary } from 'models/summaries'
import { useEffect, useRef } from 'react'

/**
 * Forwards the restaurant/Chains rich summary (LTV / places / pounds) to the
 * host banner via `setChainsSummary`, and pushes the effective row count into
 * the DataTable.
 *
 * Extracted from `ChainsTableComponent` (FIR-7728) so the eventual `<TargetTable>`
 * Chains config can run the rich summary as a side-channel — the shared table
 * needn't know about restaurant summary stats.
 *
 * `preserveWhileLoading` (the reports variant) forwards the previous values with
 * `isLoading: true` during a refetch to avoid a flicker; otherwise the update is
 * skipped while loading (the host keeps its last value).
 */
export function useForwardChainsSummary({
  summary,
  isLoading,
  count,
  preserveWhileLoading,
  setChainsSummary,
  setTotalRowsInBackend,
}: {
  summary: ChainsSummary | undefined
  isLoading: boolean
  count: number
  preserveWhileLoading: boolean
  setChainsSummary?: (summary: ChainsSummary) => void
  setTotalRowsInBackend: (count: number) => void
}) {
  // Track the previous summary so the reports variant can preserve its values
  // during a refetch instead of flashing empty.
  const previousSummaryRef = useRef<ChainsSummary>({
    total_ltv: 0,
    places_count: 0,
    count: 0,
    avg_pounds_per_year: 0,
    avg_total_ltv: 0,
    isLoading: true,
  })

  // Keep the host callbacks in refs so the forwarding effect can depend on the
  // data (isLoading/summary/count) alone without re-running — or closing over a
  // stale callback — when the host recreates these identities (e.g. an inline
  // arrow for `setChainsSummary` on the page that renders the table).
  const setChainsSummaryRef = useRef(setChainsSummary)
  setChainsSummaryRef.current = setChainsSummary
  const setTotalRowsInBackendRef = useRef(setTotalRowsInBackend)
  setTotalRowsInBackendRef.current = setTotalRowsInBackend

  useEffect(() => {
    if (isLoading) {
      if (preserveWhileLoading) {
        // Preserve previous values during loading to avoid flicker (V2).
        setChainsSummaryRef.current?.({
          ...previousSummaryRef.current,
          isLoading: true,
        })
      }
      // V1 simply skips the update while loading (the parent keeps its value).
      return
    }
    const newSummary: ChainsSummary = {
      total_ltv: summary?.total_ltv ?? 0,
      places_count: summary?.places_count ?? 0,
      count,
      avg_pounds_per_year: summary?.avg_pounds_per_year ?? 0,
      avg_total_ltv: summary?.avg_total_ltv ?? 0,
      isLoading: false,
    }
    previousSummaryRef.current = newSummary
    setChainsSummaryRef.current?.(newSummary)
    setTotalRowsInBackendRef.current(count)
  }, [isLoading, summary, count, preserveWhileLoading])
}
